/**
 * Éventail de SKILLCARDS (cover de la page d'achat d'un pack) : les cartes du
 * pack, côté verso miniature (cadre et fond dans la teinte du niveau, créature
 * origami, étoiles de rareté), déployées en arc autour d'un pivot bas.
 * Purement présentationnel. Au-delà de `FAN_MAX_CARDS`, seules les premières
 * sont montrées (« +N » sur la dernière).
 */

import type { CSSProperties } from 'react';
import {
  CARD_RARITY_STARS,
  cardPalette,
  type CardColorTheme,
  type CardRarity,
} from '@jeraisonne/db/cards';
import { OrigamiCreature } from '@jeraisonne/ui/tool-cards';

export const FAN_MAX_CARDS = 7;

export interface FanCard {
  id: string;
  creature: unknown;
  levels: string[];
  color_theme: CardColorTheme | null;
  rarity: string;
}

/**
 * Position de chaque carte, symétrique autour du centre (pure) :
 * `angle` en degrés, `shift` (horizontal) et `drop` (vertical) en % de la
 * carte, `z` = ordre d'empilement (la carte du milieu au-dessus).
 */
export function fanLayout(
  count: number,
): { angle: number; shift: number; drop: number; z: number }[] {
  if (count <= 0) return [];
  if (count === 1) return [{ angle: 0, shift: 0, drop: 0, z: 1 }];
  const spread = Math.min(44, 9 * (count - 1)); // ouverture totale de l'éventail
  const mid = (count - 1) / 2;
  return Array.from({ length: count }, (_, i) => {
    const t = i / (count - 1) - 0.5; // -0.5 → 0.5
    return {
      angle: Math.round(t * spread * 10) / 10,
      shift: Math.round(t * 48 * (count - 1)),
      drop: Math.round(t * t * 24),
      z: count - Math.round(Math.abs(i - mid) * 2),
    };
  });
}

export function PackCardFan({ cards }: { cards: readonly FanCard[] }) {
  const shown = cards.slice(0, FAN_MAX_CARDS);
  const more = cards.length - shown.length;
  const layout = fanLayout(shown.length);
  if (shown.length === 0) return null;
  return (
    <div
      aria-hidden
      data-testid="pack-card-fan"
      className="relative mx-auto h-36 w-full max-w-sm sm:h-44"
    >
      {shown.map((card, i) => {
        const palette = cardPalette(card.levels, card.color_theme);
        const stars = CARD_RARITY_STARS[card.rarity as CardRarity] ?? 0;
        const pos = layout[i]!;
        const vars = {
          '--fan-angle': `${pos.angle}deg`,
          '--fan-shift': `${pos.shift}%`,
          '--fan-drop': `${pos.drop}%`,
          zIndex: pos.z,
          '--fan-bg': palette.bg,
          '--fan-accent': palette.accent,
        } as CSSProperties;
        const last = i === shown.length - 1 && more > 0;
        return (
          <div
            key={card.id}
            style={vars}
            className="absolute bottom-0 left-1/2 flex aspect-[63/88] w-16 origin-bottom translate-x-[calc(-50%+var(--fan-shift))] translate-y-[var(--fan-drop)] rotate-[var(--fan-angle)] flex-col items-center justify-between rounded-[12%/8.6%] border-2 border-[var(--fan-accent)] bg-[radial-gradient(circle_at_50%_35%,color-mix(in_srgb,var(--fan-accent)_40%,var(--fan-bg))_0%,var(--fan-bg)_75%)] py-2 shadow-[0_10px_24px_-8px_rgba(0,0,0,0.7)] sm:w-20"
          >
            <OrigamiCreature
              creature={card.creature}
              accent={palette.accent}
              className="mt-1 h-[58%] w-[80%] drop-shadow-[0_3px_6px_rgba(0,0,0,0.45)]"
            />
            <span className="text-xs leading-none tracking-wider text-amber-400">
              {last ? `+${more}` : '★'.repeat(stars)}
            </span>
          </div>
        );
      })}
    </div>
  );
}
